/**
 * Header "Community" button — opens the Slack community.
 *
 * Two-state behaviour matched to `slackDestination()`:
 *   • Not joined yet → invite URL, label reads "Community".
 *   • Joined (localStorage flag) → workspace deep-link, label reads
 *     "Open Slack".
 *
 * Rendered inside the fixed dark app header, so styling matches the
 * other icon buttons (Help, Notifications). At <md we drop the label
 * and keep just the Slack mark so the row stays compact.
 */
import React from "react";
import {
  slackDestination,
  useSlackJoined,
  SLACK_INVITE_URL,
  SLACK_WORKSPACE_URL,
  markSlackJoined,
} from "../lib/slack";

/** Inline SVG so we don't add a new icon package dependency. Uses the
 *  official Slack 4-color mark but sized/coloured to sit on the dark
 *  header. Colours match Slack's brand palette. */
function SlackMark({ className = "w-4 h-4" }) {
  return (
    <svg
      viewBox="0 0 122.8 122.8"
      className={className}
      aria-hidden="true"
    >
      <path fill="#E01E5A" d="M25.8 77.6a12.9 12.9 0 1 1-12.9-12.9h12.9v12.9zm6.5 0a12.9 12.9 0 1 1 25.8 0v32.3a12.9 12.9 0 1 1-25.8 0V77.6z"/>
      <path fill="#36C5F0" d="M45.2 25.8a12.9 12.9 0 1 1 12.9-12.9v12.9H45.2zm0 6.5a12.9 12.9 0 1 1 0 25.8H12.9a12.9 12.9 0 1 1 0-25.8h32.3z"/>
      <path fill="#2EB67D" d="M97 45.2a12.9 12.9 0 1 1 12.9 12.9H97V45.2zm-6.5 0a12.9 12.9 0 1 1-25.8 0V12.9a12.9 12.9 0 1 1 25.8 0v32.3z"/>
      <path fill="#ECB22E" d="M77.6 97a12.9 12.9 0 1 1-12.9 12.9V97h12.9zm0-6.5a12.9 12.9 0 1 1 0-25.8h32.3a12.9 12.9 0 1 1 0 25.8H77.6z"/>
    </svg>
  );
}

export default function CommunityButton() {
  const [joined] = useSlackJoined();
  const href = slackDestination(joined);
  if (!href) return null; // no env vars → hide silently
  const label = "Community";
  const title = joined
    ? "Open the CPG Institute Slack workspace"
    : "Join the CPG Institute Slack community";
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => {
        // Any click on the workspace deep-link means they've almost
        // certainly joined, so flip the flag. For the invite URL we
        // still flip it — worst case they abandon the invite, but the
        // Members Directory card handles undo via a "Reset" toggle.
        if (href === SLACK_WORKSPACE_URL || href === SLACK_INVITE_URL) {
          markSlackJoined();
        }
      }}
      title={title}
      aria-label={title}
      data-testid="topbar-community"
      className="inline-flex items-center gap-1.5 px-2 md:px-2.5 py-1.5 rounded-md text-white hover:bg-white/10 xl:text-foreground xl:hover:bg-muted transition text-sm font-medium"
    >
      <SlackMark className="w-4 h-4 shrink-0" />
      <span className="hidden lg:inline">{label}</span>
    </a>
  );
}

export { SlackMark };
